import { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "../../hooks/useApi";
import { api, errMsg, toList, avatarSrc } from "../../lib/api";
import { PageHeader, Loader, ErrorNote, Empty, TagBadge, TAGS, positionsOf, fmt, Avatar } from "../../components/app/kit";

export default function Board() {
  const { data, loading, error, reload } = useApi("/recruiter/players");
  const [filter, setFilter] = useState("All");
  const all = toList(data, ["players"]);
  const rows = filter === "All" ? all : all.filter((p) => (p.tag ?? "Untagged") === filter);

  const remove = async (id) => {
    try { await api.delete(`/recruiter/players/${id}`); toast.success("Removed from board"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Recruiter" title="My board">
        <Link to="/recruiter/search" data-testid="board-find-prospects" className="rounded-full bg-[#39FF14] px-5 py-2.5 text-sm font-semibold text-[#0A0E1A]">Find prospects</Link>
      </PageHeader>
      <div className="mb-6 grid grid-cols-3 gap-3">
        {TAGS.map((t) => (
          <div key={t} className="rounded-xl border border-[#1E293B] bg-[#162032] px-4 py-3">
            <TagBadge tag={t} />
            <p data-testid={`board-count-${t.toLowerCase().replace(" ", "-")}`} className="mt-2 font-mono-gd text-3xl font-bold text-white">{all.filter((p) => p.tag === t).length}</p>
          </div>
        ))}
      </div>
      <div className="no-print mb-4 flex gap-1 overflow-x-auto">
        {["All", ...TAGS, "Untagged"].map((t) => (
          <button key={t} data-testid={`board-filter-${t.toLowerCase().replace(" ", "-")}`} onClick={() => setFilter(t)} className={`whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-semibold ${filter === t ? "bg-[#39FF14] text-[#0A0E1A]" : "bg-[#162032] text-slate-300"}`}>{t}</button>
        ))}
      </div>
      {loading ? <Loader /> : error ? <ErrorNote error={error} /> : !rows.length ? <Empty title="Board is empty" text="Search prospects and add them to start your board." /> : (
        <div className="overflow-x-auto rounded-2xl border border-[#1E293B]">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="bg-[#162032] font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">
              <tr>{["Prospect", "Pos", "Class", "GPA", "Team", "Notes", "Tag", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} data-testid={`board-row-${p.id}`} className="border-t border-[#1E293B] bg-[#0F172A] hover:bg-[#162032]/70">
                  <td className="px-4 py-3"><Link to={`/recruiter/prospects/${p.id}`} data-testid={`board-open-${p.id}`} className="flex items-center gap-3 font-display text-lg font-bold uppercase text-white hover:text-[#39FF14]"><Avatar name={p.name ?? ""} src={avatarSrc(p.team_id, p)} size="h-9 w-9" />{p.name}</Link></td>
                  <td className="px-4 py-3 font-mono-gd text-slate-200">{positionsOf(p)}</td>
                  <td className="px-4 py-3 font-mono-gd text-slate-200">{p.grad_year ?? "—"}</td>
                  <td className="px-4 py-3 font-mono-gd text-slate-200">{fmt(p.gpa)}</td>
                  <td className="px-4 py-3 text-slate-300">{p.team_name}</td>
                  <td className="px-4 py-3 font-mono-gd text-slate-200">{p.note_count ?? 0}</td>
                  <td className="px-4 py-3">{p.tag ? <TagBadge tag={p.tag} /> : <span className="text-slate-500">—</span>}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`board-remove-${p.id}`} onClick={() => remove(p.id)} className="rounded-full p-2 text-slate-400 hover:bg-[#FF3B30]/15 hover:text-red-200" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
